/* eslint-disable array-callback-return */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { DURATION, EASE_OUT, Reveal, pressable } from "../../../components/motion";
import "moment/locale/ja";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import moment from "moment";
import {
  addToCartReduxStore,
  deleteCartItem,
  removeFromCartReduxStore,
  checkBookingPayment,
  updateCartCustomer,
  updateStatusAuthenticatedUserBooking,
  addBooking,
  addByTheHourFormToState,
  addSpecifiedDestinationFormToState,
  reportCartTimeout
} from "../../../actions/admin/booking";
import customMoment from "../../../utils/customMoment";
import { sortCartByTravelDate } from "../../../utils/cartSort";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { withAlert, types } from "react-alert";
import { getPageContents } from "../../../actions/admin/cmspage";
import Clock from "./_components/Clock";
import NewEditBooking from "./_components/NewEditBooking";
import BookingCard from "../../../components/booking-card";
import MultiTotalCard from "../../../components/total-card/MultiTotalCard";
import { pushEventGTM } from "../../../utils/gtm";
import jwtDecode from "jwt-decode";
import { useLoading } from "../../../components/LoadingContext";
import CheckoutConfirmationModal from "./_components/CheckoutConfirmationModal";
import {
  ArrowRightIcon,
  CalendarIcon,
  ClockAltIcon,
  PlusCircleIcon,
  PlusIcon,
} from "../../../components/icons";
import { MotionModal, ModalCloseButton } from "../../../components/motion";

const visa_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/visa-icon.svg";
const jcb_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/jcb-icon.svg";
const diners_club_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/diners-club-icon.svg";
const american_express_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/amex-icon.svg";
const discover_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/discover-icon.svg";
const maestro_icon = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maestro-icon.svg";

// Deferred-payment business users are billed by monthly invoice, not by card,
// so the cart treats their total as an estimate and skips the card checkout.
// A missing or malformed token simply means "not a deferred-payment user".
const isDeferredPaymentBusinessUser = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const decodedJwt = jwtDecode(token);
    return decodedJwt.user.role === "business" && Boolean(decodedJwt.user.id.deferredPaymentEligible);
  } catch (error) {
    return false;
  }
};

// Per-card actions menu (edit / duplicate / remove). Self-contained Tailwind
// dropdown with its own open state + click-outside — avoids Bootstrap's themed
// (gold) .dropdown-item styling.
const CartActionsMenu = ({ item, editEnabled, onEdit, onDuplicate, onRemove }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const runAndClose = (fn) => () => {
    setOpen(false);
    fn();
  };

  const itemClass = "tw:w-full tw:border-0 tw:bg-white tw:cursor-pointer tw:!text-center tw:!text-[15px] tw:!text-[#282828] tw:!font-[400] tw:py-3 tw:px-2 tw:hover:!bg-[#F9F9F9]";

  return (
    <div className="tw:relative tw:inline-block tw:ml-auto" ref={menuRef}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="tw:flex tw:items-center tw:justify-center tw:bg-transparent tw:border-0 tw:p-1 tw:cursor-pointer"
      >
        <i className="fas fa-ellipsis-h tw:text-[#333]" />
      </button>

      {/* The menu drops in from the "..." button and fades back out. */}
      <AnimatePresence>
        {open && (
        <motion.ul
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ duration: DURATION.fast, ease: EASE_OUT }}
          style={{ transformOrigin: "top right" }}
          className="tw:absolute tw:right-0 tw:top-full tw:mt-2 tw:z-50 tw:min-w-[180px] tw:bg-white tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:shadow-sm tw:overflow-hidden tw:list-none tw:!p-0 tw:!m-0"
        >
          {editEnabled && (
            <>
              <li>
                <button
                  type="button"
                  className={itemClass}
                  onClick={runAndClose(onEdit)}
                >
                  Edit Booking
                </button>
              </li>

              <li className="tw:h-px tw:bg-[#E6E6E6]" />
            </>
          )}

          <li>
            <button
              type="button"
              className={itemClass}
              onClick={runAndClose(onDuplicate)}
            >
              {t("NewCart.DuplicateBooking.Title")}
            </button>
          </li>

          <li className="tw:h-px tw:bg-[#E6E6E6]" />

          <li>
            <button
              type="button"
              className={itemClass}
              onClick={runAndClose(onRemove)}
            >
              {t("NewCart.RemoveBooking.Title")}
            </button>
          </li>
        </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

// How one booking card enters and leaves the cart list. Cards rise in one
// after another (`index` staggers them); a removed card fades out sideways
// while the cards below it slide up into the gap (the `layout` prop).
const cartItemMotion = (index, shouldReduceMotion) => ({
  layout: !shouldReduceMotion,
  initial: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.base, ease: EASE_OUT, delay: Math.min(index, 6) * 0.07 },
  },
  exit: {
    opacity: 0,
    x: shouldReduceMotion ? 0 : -32,
    transition: { duration: DURATION.fast, ease: EASE_OUT },
  },
  transition: { layout: { duration: DURATION.base, ease: EASE_OUT } },
});

const NewCart = ({ alert }) => {
  const history = useHistory();
  const shouldReduceMotion = useReducedMotion();
  const { cart } = useSelector((state) => state.booking);

  // Cart items shown on screen, ordered by travel date (earliest first).
  // Only used for display — the original `cart` keeps its order.
  const sortedCart = useMemo(() => sortCartByTravelDate(cart), [cart]);

  const dispatch = useDispatch();
  const page_name = "cms-cart";
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.toUpperCase();
  // Logging in reloads the page, so reading the token on render stays current.
  const isDeferredPaymentUser = isDeferredPaymentBusinessUser();
  const [clockRef, setClockRef] = useState(null);
  const lastAddedBookToCart = localStorage.getItem("lastAddedBookToCart")
  const { showLoading, closeLoading } = useLoading();


  if (lang === "EN") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }

  useEffect(() => {
    dispatch(getPageContents(page_name));
  }, [dispatch]);

  useEffect(() => {
    if (cart) {
      cart.map((val) => {
        const items = [];
        let totalPrice = 0;

        val.bookingInfo.map((bi, ind) => {
          const item = {
            index: ind,
            item_id: bi.vehicleTypeID,
            item_name: bi.vehicleTypeName,
            item_brand: "Toyota",
            affiliation: "Flywire",
            coupon: "",
            discount: 0,
            item_category: val.bookingType === "byTheHour" ? 'By The Hour' : val.isAirportTransfer ? 'Airport Transfer' : 'Specified Destination',
            item_category2: val.destinations.length === 1 ? val.destinations[0].place_id : val.destinations[val.destinations.length - 1].place_id,
            item_category3: (bi.childSeat[0].childSeatCount + bi.childSeat[1].childSeatCount + bi.childSeat[2].childSeatCount) > 0 ? "Child Seats" : "",
            location_id: "",
            price: bi.total,
            quantity: 1,
          };

          items.push(item);
        })

        totalPrice = val.totalPrice

        const ecommerceData = {
          currency: "JPY",
          value: totalPrice,
          items: items
        }

        pushEventGTM({
          event: 'view_cart',
          ecommerce: ecommerceData
        });
      })
    }
  }, [cart])

  // Delete a single item/ booking from the cart when the delete button is clicked
  const clickRemoveItemFromcart = async (item) => {
    const items = [];
    let totalPrice = 0;

    item.bookingInfo.map((bi, ind) => {
      const itemArr = {
        index: ind,
        item_id: bi.vehicleTypeID,
        item_name: bi.vehicleTypeName,
        item_brand: "Toyota",
        affiliation: "Flywire",
        coupon: "",
        discount: 0,
        item_category: item.bookingType === "byTheHour" ? 'By The Hour' : item.isAirportTransfer ? 'Airport Transfer' : 'Specified Destination',
        item_category2: item.destinations.length === 1 ? item.destinations[0].place_id : item.destinations[item.destinations.length - 1].place_id,
        item_category3: (bi.childSeat[0].childSeatCount + bi.childSeat[1].childSeatCount + bi.childSeat[2].childSeatCount) > 0 ? "Child Seats" : "",
        location_id: "",
        price: bi.total,
        quantity: 1,
      };

      items.push(itemArr);
    })

    totalPrice = item.totalPrice

    const ecommerceData = {
      currency: "JPY",
      value: totalPrice,
      items: items
    }

    pushEventGTM({
      event: 'remove_from_cart',
      ecommerce: ecommerceData
    });

    if (localStorage.getItem("cart") !== null) {
      const curCart = cart.filter((i) => i.key !== item.key);
      dispatch(addToCartReduxStore(curCart));
      localStorage.setItem("cart", JSON.stringify(curCart));
      dispatch(removeFromCartReduxStore(curCart));
      // Remove the relevant item/ booking from the database
      await deleteCartItem(item.key);
      if (curCart?.length === 0) {
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
  };



  const checkIsAuth = async () => isDeferredPaymentBusinessUser();

  const getAuthInfo = async () => {
    const token = localStorage.getItem("token");
    const decodedJwt = jwtDecode(token);
    const accountDetail = decodedJwt?.user?.id[decodedJwt.user.role === "individual" ? "individualDetail" : "businessDetail"];

    return {
      pronounced: accountDetail.billingInformation.pronounced,
      name: accountDetail.billingInformation.name,
      email: decodedJwt.user.id.email,
      phone: accountDetail.billingInformation.phone,
      address: accountDetail.billingInformation.address,
      postCode: accountDetail.billingInformation.postal_code,
      country: accountDetail.billingInformation.country,
      country_code: accountDetail.billingInformation.country_code,
      userId: decodedJwt.user.id._id,
      prefix_number: accountDetail.billingInformation.prefix_number,
      role: decodedJwt.user.role
    }
  }

  const submitDeferredPaymentBooking = async (cart) => {
    const checkBookingPaymentResult = await checkBookingPayment(cart[0].cartID);

    if (checkBookingPaymentResult.status === "ELIGIBLE") {
      const authInfo = await getAuthInfo();

      await updateCartCustomer(
        cart,
        authInfo
      );

      const responseUpdate = await updateStatusAuthenticatedUserBooking({
        cart: cart,
        customerInfo: authInfo,
        statusBooking: "CONFIRMED",
        statusPayment: "CHARGE_PENDING",
      });

      cart.map((val) => {
        const items = [];
        let totalPrice = 0;

        val.bookingInfo.map((bi, ind) => {
          const item = {
            index: ind,
            item_id: bi.vehicleTypeID,
            item_name: bi.vehicleTypeName,
            item_brand: "Toyota",
            affiliation: "Flywire",
            coupon: "",
            discount: 0,
            item_category: val.bookingType === "byTheHour" ? 'By The Hour' : val.isAirportTransfer ? 'Airport Transfer' : 'Specified Destination',
            item_category2: val.destinations.length === 1 ? val.destinations[0].place_id : val.destinations[val.destinations.length - 1].place_id,
            item_category3: (bi.childSeat[0].childSeatCount + bi.childSeat[1].childSeatCount + bi.childSeat[2].childSeatCount) > 0 ? "Child Seats" : "",
            location_id: "",
            price: bi.total,
            quantity: 1,
          };

          items.push(item);
        })

        totalPrice = val.totalPrice

        const ecommerceData = {
          currency: "JPY",
          value: totalPrice,
          items: items
        }

        pushEventGTM({
          event: 'add_payment_info',
          ecommerce: ecommerceData
        });
      })

      closeLoading(true);
      if (responseUpdate.status === 200) {
        history.push(`/PaymentSuccess/${cart[0].cartID}`);
      }
    }
  }

  const submit = async () => {
    if (cart) {
      cart.map((val) => {
        const items = [];
        let totalPrice = 0;

        val.bookingInfo.map((bi, ind) => {
          const item = {
            index: ind,
            item_id: bi.vehicleTypeID,
            item_name: bi.vehicleTypeName,
            item_brand: "Toyota",
            affiliation: "Flywire",
            coupon: "",
            discount: 0,
            item_category: val.bookingType === "byTheHour" ? 'By The Hour' : val.isAirportTransfer ? 'Airport Transfer' : 'Specified Destination',
            item_category2: val.destinations.length === 1 ? val.destinations[0].place_id : val.destinations[val.destinations.length - 1].place_id,
            item_category3: (bi.childSeat[0].childSeatCount + bi.childSeat[1].childSeatCount + bi.childSeat[2].childSeatCount) > 0 ? "Child Seats" : "",
            location_id: "",
            price: bi.total,
            quantity: 1,
          };

          items.push(item);
        })

        totalPrice = val.totalPrice

        const ecommerceData = {
          currency: "JPY",
          value: totalPrice,
          items: items
        }
        pushEventGTM({
          event: 'begin_checkout',
          ecommerce: ecommerceData
        });
      })
    }

    const isAuth = await checkIsAuth();
    if (isAuth) {
      setCheckoutConfirmationShow(false);
      showLoading(true);
      await submitDeferredPaymentBooking(cart);
    } else {
      history.push("/checkout", {
        items_to_checkout: JSON.stringify(cart),
        fromCart: true,
      });
    }
  };

  const paymentButtomHandler = async () => {
    const isAuth = await checkIsAuth();
    if (isAuth) {
      setCheckoutConfirmationShow(true)
    } else {
      await submit();
    }
  }

  const SettingClockRef = (ref) => {
    if (ref !== null) setClockRef(ref);
  };

  const onCompleteTimer = () => {
    if (!cart || cart.length === 0) return;

    // Clearing the browser's copy is not enough: the bookings behind these items
    // exist server-side, and until they are flagged they show up in the admin
    // booking list as if the customer had gone through with them. Fire and
    // forget -- the server's sweeper flags them regardless, this just does it now.
    const keys = cart.map((item) => item.key).filter((key) => key !== undefined && key !== null);
    if (keys.length > 0) reportCartTimeout(keys);

    emptyCart();
    alert.show(
      t("Your cart has expired. Please add all items to your cart again."),
      { type: types.INFO }
    );
  };

  const start = () => {
    try {
      if (clockRef === null) return;
      clockRef.start();
    } catch (e) {
      console.error(e);
    }
  };

  const stop = () => {
    try {
      clockRef.stop();
    } catch { }
  };

  const emptyCart = () => {
    dispatch(addToCartReduxStore([]));
    localStorage.setItem("cart", JSON.stringify([]));
    dispatch(removeFromCartReduxStore([]));
  };

  const [checkoutConfirmationShow, setCheckoutConfirmationShow] = useState(false);

  const handleCheckoutConfirmationShow = () => setCheckoutConfirmationShow(true);
  const handleCheckoutConfirmationClose = () => setCheckoutConfirmationShow(false);

  // ── Edit booking modal (shared across cards, driven by the active item) ──
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editKeyBook, setEditKeyBook] = useState(null);
  const [editTypeOfTransfer, setEditTypeOfTransfer] = useState("specifiedDestination");

  const buildEditForm = (item) => {
    if (item.bookingType === "byTheHour") {
      return {
        pickuplocation: item.pickupLocation,
        dropofflocations: item.destinations,
        traveldate: customMoment(item.pickupDateTime).format("YYYY-MM-DD"),
        pickuptime: customMoment(item.pickupDateTime).format("HH:mm"),
        dropofftime: "",
        pax: {
          adult: item.pax[0].paxcount,
          child: item.pax[1].paxcount,
          toddler: item.pax[2].paxcount,
          baby: item.pax[3].paxcount,
        },
        lugage: {
          checkinSize: item.luggage[0].luggagecount,
          cabinSize: item.luggage[1].luggagecount,
        },
        duration: item.duration,
        byTheHourBook: true,
        isFromAirport: false,
        isToAirport: false,
        travelArea: item.bookingInfo[0].travelArea,
        travelAreaPrice: item.bookingInfo[0].travelAreaPrice,
      };
    }
    return {
      pickuplocation: item.pickuplocation,
      dropofflocations: item.destinations,
      traveldate: customMoment(item.travelDateWithPickupTime).format("YYYY-MM-DD"),
      pickuptime: customMoment(item.travelDateWithPickupTime).format("HH:mm"),
      pax: {
        adult: item.pax[0].paxcount,
        child: item.pax[1].paxcount,
        toddler: item.pax[2].paxcount,
        baby: item.pax[3].paxcount,
      },
      lugage: {
        checkinSize: item.luggage[0].luggagecount,
        cabinSize: item.luggage[1].luggagecount,
      },
      isAirportTransfer: item.isAirportTransfer,
      isStationTransfer: item.isStationTransfer,
    };
  };

  const openEditModal = (item) => {
    const form = buildEditForm(item);
    if (item.bookingType === "byTheHour") {
      dispatch(addByTheHourFormToState(form));
      dispatch(addSpecifiedDestinationFormToState(null));
      setEditTypeOfTransfer("byTheHour");
    } else {
      dispatch(addSpecifiedDestinationFormToState(form));
      dispatch(addByTheHourFormToState(null));
      setEditTypeOfTransfer("specifiedDestination");
    }
    setEditFormData(form);
    setEditKeyBook(item.key);
    setEditModalOpen(true);
  };

  // ── Duplicate booking modal (shared, driven by the active item) ──
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateItem, setDuplicateItem] = useState(null);
  const [duplicateInputFields, setDuplicateInputFields] = useState([""]);
  const duplicateMinDate = new Date().toISOString().slice(0, 10);

  const openDuplicateModal = (item) => {
    setDuplicateItem(item);
    setDuplicateInputFields([""]);
    setDuplicateModalOpen(true);
  };

  const closeDuplicateModal = () => {
    setDuplicateModalOpen(false);
    setDuplicateInputFields([""]);
  };

  const onChangeDuplicateDate = (e, index) => {
    const next = [...duplicateInputFields];
    next[index] = e.target.value;
    setDuplicateInputFields(next);
  };

  const removeDuplicateDate = (indexToRemove) => {
    setDuplicateInputFields((prev) => {
      const next = [...prev];
      next.splice(indexToRemove, 1);
      return next;
    });
  };

  const addDuplicateDate = () => {
    setDuplicateInputFields([...duplicateInputFields, ""]);
  };

  const changeDate = async (baseCart, duplicateCart, diffDays) => {
    duplicateCart.key = Math.floor(Math.random() * 10000000000 + 1);

    duplicateCart.pickupDateTime = customMoment(baseCart.pickupDateTime)
      .add(diffDays, "days")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]").toString();

    duplicateCart.travelDate = customMoment(baseCart.travelDate)
      .add(diffDays, "days")
      .format("YYYY-MM-DD").toString();

    duplicateCart.customerDropoffDateTime = customMoment(baseCart.customerDropoffDateTime)
      .add(diffDays, "days")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]").toString();

    duplicateCart.startFromGarage = customMoment(baseCart.startFromGarage)
      .add(diffDays, "days")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]").toString();

    duplicateCart.garageArrivalTime = customMoment(baseCart.garageArrivalTime)
      .add(diffDays, "days")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]").toString();

    duplicateCart.flightTracking = baseCart.flightTracking;

    return duplicateCart;
  };

  const recursiveChangeDate = async (obj, newDate) => {
    const date1 = customMoment(obj.pickupDateTime).format("YYYY-MM-DD");
    const date2 = customMoment(newDate).format("YYYY-MM-DD");
    const daysDifference = customMoment(date2).diff(date1, "days");
    return await changeDate(duplicateItem, obj, daysDifference);
  };

  const checkTokenUser = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedJwt = jwtDecode(token);
      if (
        (decodedJwt.user.id.role === "individual" || decodedJwt.user.id.role === "business") &&
        decodedJwt.user.id.status === "ACTIVE"
      ) {
        return decodedJwt;
      }
      return null;
    }
    return null;
  };

  const addCartToDB = async (item) => {
    const decodedJwtToken = await checkTokenUser();
    let accountDetail;
    let email;
    if (decodedJwtToken !== null) {
      accountDetail = decodedJwtToken?.user?.id[decodedJwtToken.user.id.role === "individual" ? "individualDetail" : "businessDetail"];
      email = decodedJwtToken.user.id.email;
    } else {
      email = "cart@email.com";
      accountDetail = {
        name: "cart user",
        prefix_number: "+81",
        phone: "0000000000",
        country_code: "JP",
        country: "Japan",
        address: "cart address",
        postal_code: "0000",
        city: "Tokyo",
      };
    }
    const userId = decodedJwtToken !== null ? decodedJwtToken.user.id._id : "";

    dispatch(
      addBooking({
        key: item.key,
        randomId: item.randomId,
        travelDate: item.travelDate,
        pickupTime: item.pickupTime,
        pickupDateTime: item.pickupDateTime,
        customerDropoffDateTime: item.customerDropoffDateTime,
        startFromGarage: item.startFromGarage,
        garageArrivalTime: item.garageArrivalTime,
        pickupLocation: item.pickupLocation,
        destinations: item.destinations,
        totalDistance: item.totalDistance,
        garage: item.garage._id,
        pax: item.paxList,
        luggage: item.luggagelist,
        bookingType: item.bookingType,
        additionalTime: item.additionalTime,
        breakTime: item.breakTime,
        flightTrackingTime: item.flightTrackingTime,
        usageDuration: item.usageDuration,
        billedDuration: item.billedDuration,
        duration: item.bookingType === "byTheHour" ? item.duration : null,
        isAirportTransfer: item.isAirportTransfer || item.isToAirport || item.isFromAirport,
        isFromAirport: item.isFromAirport,
        isToAirport: item.isToAirport,
        meetAssist: item.meetAssist,
        isManyPeople: item.isManyPeople,
        package: item.package,
        bookingInfo: item.bookingInfo,
        date: item.date,
        passengerName: item.passengerName === undefined ? "(your name)" : item.passengerName,
        passengerPronounced: item.passengerPronounced,
        noteList: item.message,
        totalPrice: item.totalPrice,
        pickupFlightNo: item.pickupFlightNo,
        dropoffFlightNo: item.dropoffFlightNo,
        bookingStatus: "ADDED_TO_CART",
        userId: userId,
        name: accountDetail.name,
        email: email,
        phone: accountDetail.billingInformation.phone,
        address: accountDetail.billingInformation.address,
        country: accountDetail.billingInformation.country,
        countryCodeCust: accountDetail.billingInformation.country_code,
        prefixNumber: accountDetail.billingInformation.prefix_number,
        postCode: accountDetail.billingInformation.postal_code,
      })
    );
  };

  const addItemCartToStore = async (item) => {
    const localStoreItem = JSON.parse(localStorage.getItem("cart"));
    if (localStoreItem !== null && localStoreItem.length > 0) {
      const store_cart = JSON.parse(localStorage.getItem("cart"));
      if (store_cart[0].randomId !== item.randomId) {
        item.randomId = store_cart[0].randomId;
      }
      store_cart.push(item);
      localStorage.setItem("cart", JSON.stringify(store_cart));
      dispatch(addToCartReduxStore(store_cart));
    } else {
      const cart_array_store = [item];
      localStorage.setItem("cart", JSON.stringify(cart_array_store));
      dispatch(addToCartReduxStore(cart_array_store));
    }
    localStorage.setItem("lastAddedBookToCart", customMoment());
    addCartToDB(item);
  };

  const duplicateBooking = async () => {
    if (!duplicateItem) return;
    const cartArray = [];

    await Promise.all(
      duplicateInputFields.map(async (i) => {
        if (i !== "") {
          const specificDate = new Date(i);
          const duplicatedJson = JSON.parse(JSON.stringify(duplicateItem));
          const updated = await recursiveChangeDate(duplicatedJson, specificDate);
          cartArray.push(updated);
        }
      })
    );

    await Promise.all(cartArray.map((i) => addItemCartToStore(i)));

    closeDuplicateModal();
  };

  // Per-card edit / duplicate / remove menu rendered into BookingCard's actions slot
  const renderCartActions = (c) => (
    <CartActionsMenu
      item={c}
      editEnabled={process.env.REACT_APP_EDIT_BOOKING === "true"}
      onEdit={() => openEditModal(c)}
      onDuplicate={() => openDuplicateModal(c)}
      onRemove={() => clickRemoveItemFromcart(c)}
    />
  );

  return (
    <FrontLayout>
      {/* <!-- section 1 --> */}
      <div className="tw:w-full tw:mx-auto tw:px-3 tw:!bg-[#F9F9F9] tw:mt-1 tw:pt-1">
        <div className="custom-mx">
          <div className="tw:w-full tw:mx-auto tw:px-3 tw:min-[576px]:max-w-[540px] tw:min-[768px]:max-w-[720px] tw:min-[992px]:max-w-[960px] tw:min-[1200px]:max-w-[1140px] tw:min-[1400px]:max-w-[1320px] tw:pb-12 tw:!mt-8">
            {/* <!-- column for CART ITEMS--> */}
            <div className="tw:flex tw:flex-wrap">
              <div className="tw:w-full tw:md:w-2/3 tw:pb-4 tw:lg:pb-0 tw:md:my-0">
                <div className="tw:flex tw:flex-wrap tw:-mx-3 tw:justify-between tw:items-center">
                  <div className="tw:w-full tw:md:w-7/12 tw:px-3">
                    <Reveal
                      onMount
                      y={16}
                      className="tw:font-libre-caslon-display tw:text-[26px] tw:leading-[40px] tw:!font-[400] tw:mb-0"
                    >
                      {t("NewCart.Title")} {cart === null ? '' : `(${cart?.length})`}
                    </Reveal>

                    <div className="tw:block tw:lg:hidden">
                      <hr />
                    </div>
                  </div>
                  <div className="tw:w-full tw:md:w-5/13 tw:px-3 tw:lg:mt-0 tw:text-right">
                    {cart && <Reveal onMount y={12} delay={0.08} className="tw:bg-[#F1F1F1] tw:py-3 tw:px-4 tw:rounded">
                      <div className="tw:flex tw:justify-between tw:items-center">
                        <ClockAltIcon aria-label="Clock icon" role="img" />
                        <p
                          className="tw:!p-0 tw:!m-0 tw:!text-[15px] tw:!font-[400]"
                          style={{ fontFamily: "var(--font-sans-primary)" }}
                        >
                          {t("NewCart.Countdown")}
                        </p>
                        <div className="tw:flex tw:justify-end">
                          <Clock
                            start={start}
                            stop={stop}
                            // setting={setting}
                            cart={cart}
                            refCallback={SettingClockRef}
                            onCompleteTimer={onCompleteTimer}
                            lastAddedBookToCart={lastAddedBookToCart}
                          />
                        </div>
                      </div>
                    </Reveal>}
                  </div>
                </div>
              </div>
            </div>

            {/* <!-- item card 1 --> */}
            <div className="tw:flex tw:flex-wrap tw:gap-3 tw:lg:gap-0 tw:mt-0 tw:lg:mt-6">
              <div className="tw:w-full tw:md:w-2/3 tw:p-0">
                {cart
                  ? (
                    // popLayout lifts a removed card out of the flow straight
                    // away, so the cards below can start sliding up at once.
                    <AnimatePresence mode="popLayout">
                      {sortedCart.map((c, i) => (
                        <motion.div className="tw:mb-3" key={c.key ?? i} {...cartItemMotion(i, shouldReduceMotion)}>
                          <BookingCard
                            bookingDetail={c}
                            source={"cart"}
                            actions={renderCartActions(c)}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  )
                  : <div className="tw:bg-white tw:mb-3 tw:flex tw:justify-center tw:items-center" style={{ height: "155px" }}>
                    <p className="noto-san tw:p-0 tw:m-0">{t("NewCart.NoBooking")}</p>
                  </div>}

                {/* `layout` lets this button glide up when a card is removed. */}
                <motion.div layout={!shouldReduceMotion} className="tw:grid">
                  <motion.button
                    type="button"
                    whileTap={pressable.tap}
                    className="tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none border-dashsed tw:!rounded"
                    onClick={() => {
                      history.push(`/${i18n.language || "jp"}/book`);
                    }}
                  >
                    <div className="tw:flex tw:gap-2 tw:py-2 tw:justify-center tw:items-center">
                      <PlusCircleIcon
                        size={20}
                        color={"#4E4E4E"}
                      />
                      <span>{t("Add.Another.Booking")}</span>
                    </div>
                  </motion.button>
                </motion.div>
              </div>
              <Reveal onMount y={24} delay={0.2} className="tw:w-full tw:md:w-1/3 tw:px-0 tw:md:px-6 tw:lg:px-6">
                <MultiTotalCard
                  carts={sortedCart}
                  lang={lang}
                  title={t("NewCart.Total")}
                />
                <div className="tw:mt-6">
                  {isDeferredPaymentUser ? (
                    // Invoiced users never pay by card here, so explain the total is an estimate instead.
                    <div className="tw:w-full tw:mt-4 tw:mb-3 tw:px-4 tw:text-center">
                      <p className="tw:mb-2">{t("cart.estimateNote.estimatedAmount")}</p>
                      <p className="tw:mb-0">{t("cart.estimateNote.finalInvoice")}</p>
                    </div>
                  ) : (
                    <>
                      <div className="tw:w-full tw:flex tw:items-center tw:justify-center tw:mt-4 tw:px-4">
                        <p className="tw:text-center" dangerouslySetInnerHTML={{ __html: t("Footer.AcceptPaymentMethod") }}></p>
                      </div>
                      <div className="tw:w-full tw:px-4 tw:md:flex tw:md:justify-center tw:lg:block tw:mb-3">
                        <div className="tw:border-0 tw:mb-3 tw:flex tw:flex-row tw:items-center tw:gap-2">
                          <img alt="img" src={visa_icon} className="tw:max-w-full tw:h-auto" />
                          <img alt="img" src={jcb_icon} className="tw:max-w-full tw:h-auto" />
                          <img alt="img" src={diners_club_icon} className="tw:max-w-full tw:h-auto" />
                          <img alt="img" src={american_express_icon} className="tw:max-w-full tw:h-auto" />
                          <img alt="img" src={discover_icon} className="tw:max-w-full tw:h-auto" />
                          <img alt="img" src={maestro_icon} className="tw:max-w-full tw:h-auto" />
                        </div>
                      </div>
                      <div className="tw:w-full tw:flex tw:items-center tw:justify-center tw:mt-4 tw:px-4">
                        <p className="tw:text-center" dangerouslySetInnerHTML={{ __html: t("Footer.BankTransfer") }}></p>
                      </div>
                    </>
                  )}
                </div>
                <div className="tw:grid">
                  <motion.button
                    type="button"
                    whileTap={pressable.tap}
                    className="tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:!rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:bg-[#d4b055] tw:border-[#d4b055] tw:text-[#FFFFFF] tw:hover:bg-[#c2a86b] tw:hover:border-[#c2a86b] tw:hover:opacity-60 tw:rounded tw:py-2"
                    disabled={!cart || cart?.length === 0}
                    onClick={paymentButtomHandler}
                  >
                    <div className="tw:flex tw:gap-2 tw:justify-center tw:items-center">
                      <span>{t("NewCart.ProceedToPayment")}</span>
                      <ArrowRightIcon color="#FFFFFF" size={24} />
                    </div>
                  </motion.button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
      <CheckoutConfirmationModal show={checkoutConfirmationShow} onClose={handleCheckoutConfirmationClose} onNext={submit} />

      <NewEditBooking
        useModal={editModalOpen}
        handleCloseModal={() => setEditModalOpen(false)}
        typeOfTransfer={editTypeOfTransfer}
        keyBook={editKeyBook}
        bookingFormData={editFormData}
      />

      {/* DUPLICATE BOOKING MODAL */}
      <MotionModal
        show={duplicateModalOpen}
        onClose={closeDuplicateModal}
        maxWidthClassName="tw:sm:max-w-[500px] tw:lg:max-w-[1300px]"
      >
                <div className="tw:relative tw:flex-1 tw:p-4">
                  <div className="tw:flex tw:mt-2 tw:justify-between">
                    <h5 className="tw:hidden tw:lg:block">{t("NewCart.DuplicateBooking.Title")}</h5>
                    <ModalCloseButton isInline onClick={closeDuplicateModal} />
                  </div>
                  <div className="tw:relative tw:flex tw:flex-col tw:min-w-0 tw:bg-white tw:bg-clip-border tw:border tw:border-[rgba(0,0,0,0.125)] tw:rounded-[0.3rem] border-gold">
                    <div className="tw:flex-1 tw:p-4">
                      <b>{t("BookingConfirmation.DuplicateBooking.Description")}</b>
                      <div className="tw:flex tw:flex-wrap tw:-mx-3 tw:mt-4">
                        {duplicateInputFields.map((item, index) => (
                          <div className="tw:w-full tw:min-[992px]:w-1/4 tw:px-3 tw:mb-3 input-box" key={index}>
                            <div className="tw:relative">
                              <input
                                type="date"
                                id="date"
                                name="traveldate"
                                min={duplicateMinDate}
                                className={"tw:peer tw:block tw:w-full tw:h-[calc(3.5rem+2px)] tw:leading-[1.25] tw:text-[14px] tw:text-[#4e4e4e] tw:bg-white tw:border tw:border-[#f1f1f1] tw:rounded-[0.3rem] tw:pt-[1.625rem] tw:pb-[0.625rem] tw:pr-3 tw:pl-[1.1rem] tw:placeholder-transparent tw:[font-family:var(--font-sans-primary)] tw:focus:border-[#c9c9c9] tw:focus:outline-none tw:focus:shadow-[0_0_0_0.25rem_rgba(13,110,253,0.25)] tw:disabled:bg-[rgba(167,167,167,0.1)]"}
                                placeholder="Travel date"
                                aria-label="Travel date"
                                onChange={(e) => onChangeDuplicateDate(e, index)}
                                value={duplicateInputFields[index]}
                              />
                              <i className="fa fa-close icon" onClick={() => removeDuplicateDate(index)}></i>
                              <label htmlFor="travelDate" className="tw:absolute tw:top-0 tw:left-0 tw:h-full tw:pl-4 tw:pt-4 tw:pr-3 tw:pointer-events-none tw:text-[14px] tw:text-[#a7a7a7] tw:[font-family:var(--font-sans-primary)] tw:origin-[0_0] tw:transition-[opacity,transform] tw:duration-100 tw:ease-in-out tw:scale-[0.85] tw:-translate-y-2 tw:translate-x-[0.15rem] tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:translate-y-0 tw:peer-placeholder-shown:translate-x-0 tw:peer-focus:scale-[0.85] tw:peer-focus:-translate-y-2 tw:peer-focus:translate-x-[0.15rem]">
                                <div className="tw:flex tw:items-center">
                                  <i className="tw:pr-2 tw:flex tw:self-center">
                                    <CalendarIcon color="#DBBE78" size={17} />
                                  </i>
                                  {t("Travel.Date")}
                                </div>
                              </label>
                            </div>
                          </div>
                        ))}
                        <div className="tw:w-full tw:min-[992px]:w-1/4 tw:px-3 tw:pb-4">
                          <button
                            type="button"
                            className="tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none bg-gold-light-100"
                            style={{ height: "58px" }}
                            onClick={addDuplicateDate}
                          >
                            <PlusIcon color="#666666" size={10} />
                            &nbsp; {t("BookingConfirmation.DuplicateBooking.Adddate")}
                          </button>
                        </div>
                      </div>
                      <div className="tw:flex tw:flex-wrap tw:-mx-3 tw:mt-4">
                        <p className="tw:px-3 tw:mt-4">
                          {lang === "EN" ? (
                            <> Duplicate booking for another <b>{duplicateInputFields.length} days</b></>
                          ) : (
                            <>さらに{duplicateInputFields.length}日同じ予約を作成</>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="tw:flex tw:flex-wrap tw:-mx-3 tw:justify-center tw:mt-4">
                    <div className="tw:w-full tw:min-[992px]:w-1/4 tw:px-3 tw:grid">
                      <button
                        type="button"
                        className="tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:bg-transparent tw:border-[#dbbe78] tw:text-white tw:hover:bg-[#dbbe78] tw:hover:border-[#dbbe78] tw:hover:text-[#282828] tw:rounded"
                        onClick={closeDuplicateModal}
                      >
                        {t("NewCart.DuplicateBooking.DiscardButton")}
                      </button>
                    </div>
                    <div className="tw:w-full tw:min-[992px]:w-1/4 tw:px-3 tw:grid">
                      <button
                        type="button"
                        className="tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:bg-[#d4b055] tw:border-[#d4b055] tw:text-[#4a4a4a] tw:hover:bg-[#c2a86b] tw:hover:border-[#c2a86b] tw:hover:opacity-60 tw:rounded"
                        disabled={duplicateInputFields[0] === "" || duplicateInputFields.length < 1}
                        onClick={duplicateBooking}
                      >
                        {t("NewCart.DuplicateBooking.DuplicateButton")}
                      </button>
                    </div>
                  </div>
                </div>
      </MotionModal>
    </FrontLayout>
  );
};

export default withAlert()(NewCart);
